import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  ArrowUp,
  BookmarkPlus,
  Check,
  Plus,
  Sparkles,
  X,
} from 'lucide-react'
import { ACADEMIC_ROLES, AI_ROLES, COLUMN_LABELS, CREATIVE_ROLES, generateRoundtable } from '../lib/ai'
import { cardColor } from '../lib/utils'
import { LogoMark } from './Shared'

const CREATIVE_PROMPTS = [
  '这组卡片的情感成立吗？',
  '如果要收束，应该先删哪一张？',
  '先替我做一次语言校对。',
]

const ACADEMIC_PROMPTS = [
  '把这组想法转成一个可研究的问题。',
  '它缺哪些证据、来源与反例？',
  '给我一个下一步研究设计。',
]

function RoleSeat({ role }) {
  return (
    <div className={'rt-persona' + (role.group === 'academic' ? ' is-academic' : '')}>
      <span className="rt-persona__avatar" style={{ background: role.color }}>{role.initials}</span>
      <div>
        <strong>{role.name}<em>{role.title}</em></strong>
        <p>{role.line}</p>
      </div>
      <i />
    </div>
  )
}

function buildInitialContext(project, ids) {
  const picked = (ids || []).map((id) => project.cards.find((card) => card.id === id)).filter(Boolean)
  if (picked.length >= 2) return picked.slice(0, 4)
  const seed = picked[0] || project.cards.find((card) => card.column === 'polish') || project.cards[0]
  if (!seed) return []
  const linkedIds = new Set()
  project.connections.forEach((link) => {
    if (link.source === seed.id) linkedIds.add(link.target)
    if (link.target === seed.id) linkedIds.add(link.source)
  })
  const linked = project.cards.filter((card) => linkedIds.has(card.id))
  const fallback = project.cards.filter((card) => card.id !== seed.id && !linkedIds.has(card.id))
  return [seed, ...linked, ...fallback].slice(0, Math.max(2, Math.min(4, 1 + linked.length))).slice(0, 4)
}

export default function Roundtable({ project, initialCardIds, onClose, onSaveNote, onOpenFinish, notify }) {
  const initialContext = useMemo(() => buildInitialContext(project, initialCardIds), [])
  const [context, setContext] = useState(initialContext)
  const [query, setQuery] = useState('')
  const [rounds, setRounds] = useState(() => {
    const intro = generateRoundtable({ project, context: initialContext, isIntro: true })
    return [{
      id: 'round-1',
      index: 1,
      label: '第 01 轮 · 先一起读一遍',
      user: '',
      messages: intro.messages,
      synthesis: intro.synthesis,
      loading: false,
    }]
  })
  const [thinking, setThinking] = useState(false)
  const [roundCount, setRoundCount] = useState(1)
  const [addingContext, setAddingContext] = useState(false)
  const [savedNotes, setSavedNotes] = useState(() => new Set())
  const scrollRef = useRef(null)
  const textareaRef = useRef(null)
  const timerRef = useRef(null)
  const contextIds = context.map((card) => card.id)

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    const node = scrollRef.current
    if (!node) return
    if (rounds.length === 1 && !thinking) return
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
  }, [rounds, thinking])

  const available = project.cards.filter((card) => !contextIds.includes(card.id))

  function send(override) {
    const text = (override ?? query).trim()
    if (!text || thinking) return
    const nextRound = roundCount + 1
    const round = {
      id: 'round-' + Date.now(),
      index: nextRound,
      label: '第 ' + String(nextRound).padStart(2, '0') + ' 轮 · 正在翻卡片…',
      user: text,
      messages: [],
      synthesis: null,
      loading: true,
    }
    setRounds((current) => [...current, round])
    setQuery('')
    setThinking(true)
    setRoundCount(nextRound)
    setAddingContext(false)
    const snapshot = context
    timerRef.current = setTimeout(() => {
      const result = generateRoundtable({ project, context: snapshot, query: text })
      setRounds((current) => current.map((item) => item.id === round.id ? {
        ...item,
        label: '第 ' + String(nextRound).padStart(2, '0') + ' 轮 · ' + result.roundTitle,
        messages: result.messages,
        synthesis: result.synthesis,
        loading: false,
      } : item))
      setThinking(false)
    }, 900)
  }

  function onTextKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  function addContext(card) {
    if (context.length >= 5) {
      notify('桌上已经有五张卡片了，先移走一张吧。')
      return
    }
    setContext((current) => [...current, card])
    setAddingContext(false)
  }

  function removeContext(id) {
    if (context.length <= 1) {
      notify('至少留一张卡片在桌上。')
      return
    }
    setContext((current) => current.filter((card) => card.id !== id))
  }

  function saveNote(message, index, roundId) {
    if (!message.note || !context[0]) return
    onSaveNote(context[0].id, '【AI 圆桌 · ' + AI_ROLES.find((role) => role.id === message.role)?.name + '】' + message.note)
    setSavedNotes((current) => new Set(current).add(roundId + '-' + index))
  }

  return (
    <div className="rt-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <div className="roundtable" role="dialog" aria-modal="true" aria-label="AI 圆桌对话">
        <aside className="rt-side">
          <div className="rt-brand">
            <LogoMark size={32} />
            <div>
              <strong>AI 圆桌</strong>
              <span>THE ROUND TABLE</span>
            </div>
            <button className="rt-side-close" onClick={onClose} aria-label="关闭圆桌"><X size={16} /></button>
          </div>

          <div className="rt-side-section">
            <div className="rt-side-label"><span>在座的成员</span><span>{String(AI_ROLES.length).padStart(2, '0')}</span></div>
            <div className="rt-personas">
              <div className="rt-persona-group-label"><span>创作编辑</span><em>{String(CREATIVE_ROLES.length).padStart(2, '0')}</em></div>
              {CREATIVE_ROLES.map((role) => <RoleSeat key={role.id} role={role} />)}
              <div className="rt-persona-group-label is-academic"><span>学术顾问</span><em>RESEARCH · {String(ACADEMIC_ROLES.length).padStart(2, '0')}</em></div>
              {ACADEMIC_ROLES.map((role) => <RoleSeat key={role.id} role={role} />)}
            </div>
          </div>

          <div className="rt-side-section rt-context-section">
            <div className="rt-side-label">
              <span>桌上的卡片</span>
              <span>{String(context.length).padStart(2, '0')}</span>
            </div>
            <div className="rt-context-list">
              {context.map((card) => (
                <div className="rt-context-card" key={card.id} style={{ '--card-tint': cardColor(card) }}>
                  <span className="rt-context-dot" style={{ background: cardColor(card) }} />
                  <div>
                    <strong>{card.title}</strong>
                    <em>{COLUMN_LABELS[card.column]} · {card.act?.split(' · ')[0]}</em>
                  </div>
                  <button onClick={() => removeContext(card.id)} aria-label={'移出 ' + card.title}><X size={12} /></button>
                </div>
              ))}
            </div>
            <button className="rt-add-context" onClick={() => setAddingContext(!addingContext)}>
              <Plus size={13} /> 添加卡片
            </button>
            {addingContext ? (
              <div className="rt-context-picker">
                {available.length ? available.map((card) => (
                  <button key={card.id} onClick={() => addContext(card)}>
                    <span>{COLUMN_LABELS[card.column]}</span>
                    {card.title}
                  </button>
                )) : <p>所有卡片都已经在桌上了。</p>}
              </div>
            ) : null}
          </div>

          <div className="rt-side-foot">
            <div className="rt-side-foot__note">
              <span>圆桌读的是你的卡片，不会替你做决定。</span>
            </div>
            <button className="rt-finish-link" onClick={onOpenFinish}>
              <Sparkles size={14} /> 去收束工作室
              <ArrowRight size={13} />
            </button>
          </div>
        </aside>

        <section className="rt-main">
          <header className="rt-header">
            <div>
              <div className="rt-header__eyebrow"><span className="live-dot" /> ROUNDTABLE IN SESSION</div>
              <h2>{project.title}<span>/ 圆桌纪要</span></h2>
            </div>
            <div className="rt-header__right">
              <div className="rt-header-avatars">
                {AI_ROLES.map((role) => (
                  <span key={role.id} style={{ background: role.color }} title={role.name + ' · ' + role.title}>{role.initials}</span>
                ))}
              </div>
              <span className="rt-header__status">{AI_ROLES.length} 位成员在线</span>
              <button className="icon-btn" onClick={onClose} aria-label="关闭圆桌"><X size={18} /></button>
            </div>
          </header>

          <div className="rt-scroll" ref={scrollRef}>
            <div className="rt-welcome">
              <span className="rt-welcome__rule" />
              <p>把问题放在桌上。<br /><em>创作编辑读结构与感受，学术顾问追问方法与证据。</em></p>
              <span className="rt-welcome__rule" />
            </div>

            {rounds.map((round) => (
              <div className="rt-round" key={round.id}>
                <div className="rt-round-label"><span>ROUND {String(round.index).padStart(2, '0')}</span><i />{round.label.replace(/^第 \d+ 轮 · /, '')}</div>

                {round.user ? (
                  <div className="rt-user-row">
                    <div className="rt-user-bubble">
                      <span>你</span>
                      <p>{round.user}</p>
                    </div>
                  </div>
                ) : null}

                {round.loading ? (
                  <div className="rt-thinking">
                    <div className="rt-thinking__avatars">
                      {AI_ROLES.map((role) => <span key={role.id} style={{ background: role.color }} title={role.name + ' · ' + role.title}>{role.initials}</span>)}
                    </div>
                    <div>
                      <strong>圆桌正在翻你的卡片</strong>
                      <span>结构、语气、反例、研究方法与文献证据正在同时展开<i className="dots"><b /><b /><b /></i></span>
                    </div>
                  </div>
                ) : round.messages.map((message, index) => {
                  const role = AI_ROLES.find((item) => item.id === message.role)
                  const noteKey = round.id + '-' + index
                  return (
                    <div className={'rt-message' + (role.group === 'academic' ? ' is-academic' : '')} key={message.role + '-' + index}>
                      <div className="rt-message__avatar" style={{ background: role.color }}>{role.initials}</div>
                      <div className="rt-message__body">
                        <div className="rt-message__head">
                          <strong>{role.name}</strong>
                          <span>{role.title}</span>
                          <em>{role.code}</em>
                        </div>
                        {message.paragraphs.map((paragraph, pIndex) => <p key={pIndex}>{paragraph}</p>)}
                        {message.bullets?.length ? (
                          <ul className="rt-bullets">
                            {message.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                          </ul>
                        ) : null}
                        {message.note ? (
                          <div className="rt-note-line">
                            <span>落点</span>
                            <p>{message.note}</p>
                            <button
                              className={savedNotes.has(noteKey) ? 'is-saved' : ''}
                              onClick={() => saveNote(message, index, round.id)}
                              disabled={savedNotes.has(noteKey)}
                            >
                              {savedNotes.has(noteKey) ? <Check size={12} /> : <BookmarkPlus size={12} />}
                              {savedNotes.has(noteKey) ? '已存入卡片批注' : '存为卡片批注'}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}

                {round.synthesis ? (
                  <div className="rt-synthesis">
                    <div className="rt-synthesis__head">
                      <span><Sparkles size={14} /> 本轮纪要</span>
                      <em>AI 整理 · 非最终决定</em>
                    </div>
                    <div className="rt-synthesis__grid">
                      <div><span>共识</span><p>{round.synthesis.consensus}</p></div>
                      <div><span>分歧</span><p>{round.synthesis.tension}</p></div>
                      <div className="rt-next"><span>下一小步</span><p>{round.synthesis.next}</p></div>
                    </div>
                    {round.synthesis.research ? (
                      <div className="rt-research-action">
                        <span>研究动作</span>
                        <p>{round.synthesis.research}</p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="rt-composer-wrap">
            <div className="rt-composer">
              <div className="rt-prompt-rail" aria-label="快捷议题">
                <span className="rt-prompt-rail__label"><Sparkles size={11} /> 试问</span>
                {ACADEMIC_PROMPTS.map((prompt) => (
                  <button key={prompt} className="is-research" onClick={() => send(prompt)} disabled={thinking} title={prompt}>{prompt}</button>
                ))}
                <span className="rt-prompt-rail__sep" />
                {CREATIVE_PROMPTS.map((prompt) => (
                  <button key={prompt} onClick={() => send(prompt)} disabled={thinking} title={prompt}>{prompt}</button>
                ))}
              </div>
              <div className="rt-composer__context">
                <span>{context.length} 张卡片在桌上</span>
                <i />
                <span>Enter 发送 · Shift + Enter 换行</span>
              </div>
              <div className="rt-composer__input-row">
                <textarea
                  ref={textareaRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onTextKeyDown}
                  placeholder="把你正在犹豫的问题，放在桌上……"
                  rows={2}
                  disabled={thinking}
                />
                <button className="rt-send" onClick={() => send()} disabled={!query.trim() || thinking} aria-label="发送问题">
                  <ArrowUp size={18} />
                </button>
              </div>
              <div className="rt-composer__foot">
                <span><span className="live-dot" /> 围绕 {context.length} 张卡片展开</span>
                <button onClick={onOpenFinish}><Sparkles size={12} /> 我需要收束</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
